// 파일 타입 정보 인터페이스
interface FileTypeInfo {
  icon: string;
  label: string;
  color: string;
}

// 파일 확장자별 정보 매핑
const FILE_TYPE_MAP: Record<string, FileTypeInfo> = {
  // Microsoft Office
  'docx': { icon: '📄', label: 'Word 문서', color: '#2b579a' },
  'doc': { icon: '📄', label: 'Word 문서', color: '#2b579a' },
  'xlsx': { icon: '📊', label: 'Excel 문서', color: '#217346' },
  'xls': { icon: '📊', label: 'Excel 문서', color: '#217346' },
  'pptx': { icon: '📽️', label: 'PowerPoint 문서', color: '#d24726' },
  'ppt': { icon: '📽️', label: 'PowerPoint 문서', color: '#d24726' },
  
  // PDF
  'pdf': { icon: '📋', label: 'PDF 문서', color: '#dc3545' },
  
  // 이미지
  'jpg': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'jpeg': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'png': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'gif': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'bmp': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'webp': { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  
  // 비디오
  'mp4': { icon: '🎬', label: '비디오', color: '#fd7e14' },
  'avi': { icon: '🎬', label: '비디오', color: '#fd7e14' },
  'mov': { icon: '🎬', label: '비디오', color: '#fd7e14' },
  'wmv': { icon: '🎬', label: '비디오', color: '#fd7e14' },
  
  // 오디오
  'mp3': { icon: '🎵', label: '오디오', color: '#20c997' },
  'wav': { icon: '🎵', label: '오디오', color: '#20c997' },
  'flac': { icon: '🎵', label: '오디오', color: '#20c997' },
  
  // 압축 파일
  'zip': { icon: '📦', label: '압축 파일', color: '#6c757d' },
  'rar': { icon: '📦', label: '압축 파일', color: '#6c757d' },
  '7z': { icon: '📦', label: '압축 파일', color: '#6c757d' },
  'tar': { icon: '📦', label: '압축 파일', color: '#6c757d' },
  'gz': { icon: '📦', label: '압축 파일', color: '#6c757d' },
  
  // 텍스트
  'txt': { icon: '📝', label: '텍스트', color: '#495057' },
  'rtf': { icon: '📝', label: '텍스트', color: '#495057' },
  
  // 코드
  'html': { icon: '🌐', label: 'HTML', color: '#e34c26' },
  'css': { icon: '🎨', label: 'CSS', color: '#1572b6' },
  'js': { icon: '⚡', label: 'JavaScript', color: '#f7df1e' },
  'ts': { icon: '⚡', label: 'TypeScript', color: '#3178c6' },
  'json': { icon: '📋', label: 'JSON', color: '#000000' },
  'xml': { icon: '📋', label: 'XML', color: '#000000' },
};

// MIME 타입별 정보 매핑 (확장자로 식별되지 않는 경우 사용)
const MIME_TYPE_MAP: Record<string, FileTypeInfo> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 
    { icon: '📄', label: 'Word 문서', color: '#2b579a' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 
    { icon: '📊', label: 'Excel 문서', color: '#217346' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 
    { icon: '📽️', label: 'PowerPoint 문서', color: '#d24726' },
  'application/msword': 
    { icon: '📄', label: 'Word 문서', color: '#2b579a' },
  'application/vnd.ms-excel': 
    { icon: '📊', label: 'Excel 문서', color: '#217346' },
  'application/vnd.ms-powerpoint': 
    { icon: '📽️', label: 'PowerPoint 문서', color: '#d24726' },
  'application/pdf': 
    { icon: '📋', label: 'PDF 문서', color: '#dc3545' },
  'text/plain': 
    { icon: '📝', label: '텍스트', color: '#495057' },
  'application/zip': 
    { icon: '📦', label: '압축 파일', color: '#6c757d' },
  'application/x-rar-compressed': 
    { icon: '📦', label: '압축 파일', color: '#6c757d' },
  'image/jpeg': 
    { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'image/png': 
    { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'image/gif': 
    { icon: '🖼️', label: '이미지', color: '#6f42c1' },
  'video/mp4': 
    { icon: '🎬', label: '비디오', color: '#fd7e14' },
  'audio/mpeg': 
    { icon: '🎵', label: '오디오', color: '#20c997' },
};

// 기본 파일 정보
const DEFAULT_FILE_INFO: FileTypeInfo = {
  icon: '📎',
  label: '파일',
  color: '#6c757d'
};

/**
 * 파일명과 MIME 타입을 기반으로 파일 타입 정보를 반환합니다.
 * @param fileName 파일명
 * @param mimeType MIME 타입
 * @returns 파일 타입 정보 (아이콘, 라벨, 색상)
 */
export const getFileTypeInfo = (fileName: string, mimeType?: string): FileTypeInfo => {
  // 1. 파일 확장자로 먼저 확인
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (extension && FILE_TYPE_MAP[extension]) {
    return FILE_TYPE_MAP[extension];
  }
  
  // 2. MIME 타입으로 확인
  if (mimeType && MIME_TYPE_MAP[mimeType]) {
    return MIME_TYPE_MAP[mimeType];
  }
  
  // 3. 기본값 반환
  return DEFAULT_FILE_INFO;
};

/**
 * 파일 크기를 사람이 읽기 쉬운 형태로 변환합니다.
 * @param bytes 바이트 크기
 * @returns 변환된 크기 문자열
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * 파일명을 안전하게 표시하기 위해 길이를 제한합니다.
 * @param fileName 파일명
 * @param maxLength 최대 길이 (기본값: 50)
 * @returns 제한된 길이의 파일명
 */
export const truncateFileName = (fileName: string, maxLength: number = 50): string => {
  if (fileName.length <= maxLength) return fileName;
  
  const extension = fileName.split('.').pop();
  const name = fileName.substring(0, fileName.lastIndexOf('.'));
  const truncatedName = name.substring(0, maxLength - extension!.length - 4); // 4는 "..." + "." 길이
  
  return `${truncatedName}...${extension}`;
}; 